import { useEffect, useMemo, useRef, useState } from 'react';
import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { normalizeSearchableText } from './utils.js';
import { HtmlContent } from './Listing.jsx';
import { FileViewerPortal, isViewableFile } from './FileViewer.jsx';

export function readFieldControlValue(control) {
  if (!control) {
    return '';
  }

  if (control instanceof HTMLInputElement) {
    if (control.type === 'checkbox') {
      return control.checked ? 'Oui' : 'Non';
    }

    if (control.type === 'file') {
      return Array.from(control.files || []).map((file) => file.name).join(', ');
    }

    return control.value || '';
  }

  if (control instanceof HTMLTextAreaElement) {
    return control.value || '';
  }

  if (control instanceof HTMLSelectElement) {
    return Array.from(control.selectedOptions || [])
      .map((option) => option.textContent?.trim() || option.value)
      .filter(Boolean)
      .join(', ');
  }

  if (control instanceof HTMLAnchorElement) {
    return control.textContent?.trim() || control.href || '';
  }

  return control.textContent?.trim() || '';
}

export function readFileWidgetFields(itemEl) {
  const fields = {};

  itemEl.querySelectorAll('label').forEach((labelEl) => {
    const label = labelEl.textContent?.trim();
    const targetId = labelEl.getAttribute('for');

    if (!label || !targetId) {
      return;
    }

    const control = document.getElementById(targetId);
    const value = readFieldControlValue(control);
    if (value) {
      fields[label] = value;
    }
  });

  // Try to find download URL from a rendered <a> link first
  const linkEl = itemEl.querySelector('a[href]');
  if (linkEl instanceof HTMLAnchorElement) {
    fields.__download_url = linkEl.href || '';
    fields.__download_label = linkEl.textContent?.trim() || linkEl.href || '';
  }

  // Fallback: if no <a> was found, check hidden inputs whose value looks like a URL.
  // The DynamicButtonLinkField stores the URL in a hidden input even when the
  // ButtonLinkField anchor is not rendered (e.g. after form rebuild via fetch).
  if (!fields.__download_url) {
    itemEl.querySelectorAll('input[type="hidden"]').forEach((input) => {
      if (fields.__download_url) return;
      const val = input.value || '';
      if (val && (val.startsWith('http://') || val.startsWith('https://') || val.startsWith('internal:/'))) {
        fields.__download_url = val;
        fields.__download_label = 'Télécharger';
      }
    });
  }

  return fields;
}

export function findFieldValue(fields, patterns) {
  const normalizedPatterns = patterns.map((pattern) => normalizeSearchableText(pattern));

  for (const [label, value] of Object.entries(fields || {})) {
    if (label.startsWith('__') || !value) {
      continue;
    }

    const normalizedLabel = normalizeSearchableText(label);
    if (normalizedPatterns.some((pattern) => normalizedLabel.includes(pattern))) {
      return String(value).trim();
    }
  }

  return '';
}

export function readSelectedFileNames(inputEl) {
  if (!(inputEl instanceof HTMLInputElement)) {
    return '';
  }

  return Array.from(inputEl.files || []).map((file) => file.name).join(', ');
}

export function readFileWidgetRows(rootEl) {
  if (!(rootEl instanceof HTMLElement)) {
    return [];
  }

  return Array.from(rootEl.querySelectorAll('[data-cst-file-widget-item="1"]')).map((itemEl, index) => {
    const modalId = itemEl.dataset.cstFileWidgetModalId || '';
    // Modal may still be inside the wrapper or may have been moved to <body>
    const modalEl = modalId ? document.getElementById(modalId) : null;
    const contentEl = modalEl || itemEl;

    const summary = contentEl.querySelector('.modal-title')?.textContent?.trim()
      || contentEl.querySelector('summary')?.textContent?.trim()
      || `Fichier ${index + 1}`;
    const fields = readFileWidgetFields(contentEl);

    return {
      id: String(itemEl.dataset.cstFileWidgetItemIndex || index),
      modalId,
      summary,
      fields,
      downloadUrl: fields.__download_url || '',
      downloadLabel: fields.__download_label || '',
    };
  });
}

export function readFileWidgetSettings(hostEl) {
  if (!(hostEl instanceof HTMLElement)) {
    return null;
  }

  const rootEl = hostEl.closest('[data-cst-file-widget-root="1"]');
  if (!(rootEl instanceof HTMLElement)) {
    return null;
  }

  return {
    rootEl,
    key: hostEl.dataset.cstFileWidgetKey || '',
    wrapper: hostEl.dataset.cstFileWidgetWrapper || '',
    label: rootEl.dataset.cstFileWidgetLabel || '',
    emptyLabel: hostEl.dataset.cstFileWidgetEmptyLabel || 'Aucun fichier pour le moment.',
    emptyHint: hostEl.dataset.cstFileWidgetEmptyHint || 'Ajoutez un premier fichier pour demarrer.',
    uploadLabel: hostEl.dataset.cstFileWidgetUploadLabel || 'Ajouter un fichier',
    uploadHint: hostEl.dataset.cstFileWidgetUploadHint || 'Le fichier sera ajoute a la liste apres validation. Vous pourrez ensuite completer ses metadonnees si besoin.',
    editLabel: hostEl.dataset.cstFileWidgetEditLabel || 'Modifier',
    downloadLabel: hostEl.dataset.cstFileWidgetDownloadLabel || 'Telecharger',
    closeLabel: hostEl.dataset.cstFileWidgetCloseLabel || 'Fermer',
    browseLabel: hostEl.dataset.cstFileWidgetBrowseLabel || 'Choisir un fichier',
    submitLabel: hostEl.dataset.cstFileWidgetSubmitLabel || 'Envoyer le fichier',
    resetLabel: hostEl.dataset.cstFileWidgetResetLabel || 'Retirer',
    dropzoneLabel: hostEl.dataset.cstFileWidgetDropzoneLabel || 'Deposez un fichier ici',
    dropzoneHint: hostEl.dataset.cstFileWidgetDropzoneHint || 'ou cliquez pour parcourir vos fichiers',
    pendingLabel: hostEl.dataset.cstFileWidgetPendingLabel || 'Fichier pret a etre envoye',
  };
}

export function findFileWidgetNativeElements(rootEl) {
  const inputEl = rootEl.querySelector('[data-cst-file-widget-input="1"]');
  const addButtonEl = rootEl.querySelector('[data-cst-file-widget-add="1"]');
  const uploadInfoEl = rootEl.querySelector('[data-cst-file-widget-upload-info="1"]');

  return {
    inputEl,
    addButtonEl,
    uploadInfoEl,
  };
}

export function triggerNativeFileWidgetSubmit(buttonEl) {
  if (!(buttonEl instanceof HTMLElement)) {
    return false;
  }

  if (buttonEl.hasAttribute('disabled')) {
    return false;
  }

  if (typeof window !== 'undefined' && typeof window.jQuery === 'function') {
    window.jQuery(buttonEl).trigger('mousedown');
    return true;
  }

  if (typeof MouseEvent === 'function') {
    buttonEl.dispatchEvent(new MouseEvent('mousedown', {
      bubbles: true,
      cancelable: true,
      view: window,
    }));
    return true;
  }

  buttonEl.click();
  return true;
}

export function assignFilesToNativeInput(inputEl, files) {
  if (!(inputEl instanceof HTMLInputElement) || inputEl.type !== 'file') {
    return false;
  }

  if (!files || !files.length || typeof DataTransfer !== 'function') {
    return false;
  }

  try {
    const transfer = new DataTransfer();
    Array.from(files).forEach((file) => transfer.items.add(file));
    inputEl.files = transfer.files;
    inputEl.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  } catch (error) {
    console.warn('[cst_admin_tools] unable to assign dropped files to the native input', error);
    return false;
  }
}

const ICON_DOWNLOAD = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>';
const ICON_EYE = '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>';

/**
 * Hide a Drupal form element and all its ancestor wrappers up to (but not
 * including) the modal-body.  Drupal wraps each field in several nested divs
 * so hiding just the closest parent is not enough.
 */
function hideFieldInModalBody(el) {
  let node = el;
  while (node && node.parentElement) {
    const parent = node.parentElement;
    if (parent.classList.contains('modal-body')) {
      node.style.display = 'none';
      return;
    }
    node = parent;
  }
  el.style.display = 'none';
}

/**
 * Restructure a file-widget edit modal:
 * - Ensure close X button in header
 * - Move download link from body into footer with icons
 * (The Supprimer action lives in the sibling {modalId}-confirm modal.)
 */
function restructureFileModal(modalEl, { onView } = {}) {
  // ── Header close button ──
  const header = modalEl.querySelector('.modal-header');
  if (header && !header.querySelector('.cat-eamf-close-btn')) {
    header.querySelectorAll('.btn-close, [data-bs-dismiss]').forEach((el) => el.remove());

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'cat-eamf-close-btn';
    closeBtn.setAttribute('aria-label', 'Fermer');
    closeBtn.innerHTML = '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
    closeBtn.addEventListener('click', () => {
      if (window.bootstrap?.Modal) {
        const inst = window.bootstrap.Modal.getInstance(modalEl);
        if (inst) { inst.hide(); return; }
      }
      if (window.jQuery) { window.jQuery(modalEl).modal('hide'); }
    });
    header.appendChild(closeBtn);
  }

  // ── Footer ──
  const footer = modalEl.querySelector('.modal-footer');
  if (!footer) return;

  // Remove old "Fermer" text button
  footer.querySelectorAll('button, .btn').forEach((el) => el.remove());

  // — Download link —
  const downloadLink = modalEl.querySelector('.modal-body a[href]');
  if (downloadLink) {
    hideFieldInModalBody(downloadLink);

    const dlBtn = document.createElement('a');
    dlBtn.href = downloadLink.href;
    dlBtn.target = '_blank';
    dlBtn.rel = 'noreferrer';
    dlBtn.className = 'cat-eamf-footer-action';
    dlBtn.innerHTML = ICON_DOWNLOAD + ' <span>Télécharger</span>';
    footer.appendChild(dlBtn);

    // — View button (images & PDFs only) —
    if (onView) {
      const title = modalEl.querySelector('.modal-title')?.textContent?.trim() || '';
        const viewBtn = document.createElement('button');
        viewBtn.type = 'button';
        viewBtn.className = 'cat-eamf-footer-action';
        viewBtn.innerHTML = ICON_EYE + ' <span>Visualiser</span>';
        viewBtn.addEventListener('click', () => onView(downloadLink.href, title));
        footer.appendChild(viewBtn);
    }
  }

}

export function FileCollectionWidget({ hostEl }) {
  const settings = useMemo(() => readFileWidgetSettings(hostEl), [hostEl]);
  const [rows, setRows] = useState(() => readFileWidgetRows(settings?.rootEl));
  const [selectedFileName, setSelectedFileName] = useState('');
  const [uploadInfoHtml, setUploadInfoHtml] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [dropError, setDropError] = useState('');
  const [viewer, setViewer] = useState(null);
  const dragDepthRef = useRef(0);
  const nativeRefs = useRef({
    inputEl: null,
    addButtonEl: null,
  });

  function openFileModal(modalId) {
    if (!modalId) return;
    const modalEl = document.getElementById(modalId);
    if (!modalEl) return;
    if (window.bootstrap?.Modal) {
      const instance = window.bootstrap.Modal.getInstance(modalEl) || new window.bootstrap.Modal(modalEl);
      instance.show();
    } else if (window.jQuery) {
      window.jQuery(modalEl).modal('show');
    }
  }

  function clearPendingFile() {
    if (nativeRefs.current.inputEl instanceof HTMLInputElement) {
      nativeRefs.current.inputEl.value = '';
    }

    setSelectedFileName('');
    setDropError('');
  }

  function submitPendingFile() {
    setDropError('');

    // Use the filename already in state rather than re-reading from native input
    // (the native input may have been cleared by a previous read)
    const { inputEl } = nativeRefs.current;
    const nextFileName = readSelectedFileNames(inputEl);
    if (!nextFileName) {
      return;
    }

    // Search broadly for the submit button
    const root = settings?.rootEl;
    let btn = nativeRefs.current.addButtonEl;
    if (!(btn instanceof HTMLElement) || !btn.isConnected) {
      btn = root?.querySelector('[data-cst-file-widget-add="1"]')
        || root?.querySelector('.cst-file-widget__native-submit')
        || root?.querySelector('input[type="submit"].nested_btn_add')
        || root?.closest('form')?.querySelector('[data-cst-file-widget-add="1"]');
      if (btn) {
        nativeRefs.current.addButtonEl = btn;
      }
    }

    if (!(btn instanceof HTMLElement)) {
      setDropError('Erreur: bouton introuvable. Rechargez la page.');
      return;
    }

    setIsUploading(true);

    // Use requestAnimationFrame to ensure React renders the "uploading"
    // state before we submit the form (which causes a full page reload).
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        btn.click();
      });
    });
  }

  function handleDrop(event) {
    if (isUploading) {
      return;
    }

    event.preventDefault();
    dragDepthRef.current = 0;
    setIsDragOver(false);
    setDropError('');

    const files = event.dataTransfer?.files;
    if (!files?.length) {
      return;
    }

    const assigned = assignFilesToNativeInput(nativeRefs.current.inputEl, files);
    if (!assigned) {
      setDropError("Le depot direct n'est pas disponible ici. Utilisez le bouton de selection.");
      return;
    }

    // Show upload animation immediately and trigger upload — skip the pending state entirely
    // Show uploading state immediately
    setSelectedFileName(readSelectedFileNames(nativeRefs.current.inputEl));
    setIsUploading(true);

    // Find the submit button and form
    const root = settings?.rootEl;
    const btn = nativeRefs.current.addButtonEl
      || root?.querySelector('[data-cst-file-widget-add="1"]')
      || root?.querySelector('.cst-file-widget__native-submit');
    const form = btn?.closest('form');

    if (!btn || !form) {
      setIsUploading(false);
      setDropError('Erreur: bouton ou formulaire introuvable.');
      return;
    }

    // Submit after a short delay so React can paint the spinner
    window.setTimeout(() => {
      // Add hidden input with button name/value so Drupal identifies the trigger
      const hidden = document.createElement('input');
      hidden.type = 'hidden';
      hidden.name = btn.name;
      hidden.value = btn.value;
      form.appendChild(hidden);

      // Native submit — bypasses all jQuery/Drupal AJAX interception
      HTMLFormElement.prototype.submit.call(form);
    }, 200);
  }

  useEffect(() => {
    if (!settings?.rootEl) {
      return undefined;
    }

    const { rootEl } = settings;
    const { inputEl, addButtonEl, uploadInfoEl } = findFileWidgetNativeElements(rootEl);
    const refreshRows = () => setRows(readFileWidgetRows(rootEl));
    const handleNativeInputChange = () => {
      setSelectedFileName(readSelectedFileNames(inputEl));
      setDropError('');
    };

    rootEl.dataset.cstFileWidgetMounted = '1';
    nativeRefs.current = { inputEl, addButtonEl };
    setSelectedFileName(readSelectedFileNames(inputEl));
    setUploadInfoHtml(uploadInfoEl?.innerHTML || '');
    refreshRows();

    // Move all file-widget modals (edit + confirm) from the form to <body> so
    // Bootstrap can position them correctly (no ancestor transform/overflow
    // interference). Both edit and confirm modals are tagged
    // data-cst-sidebar-modal-managed="1" so file-widget-autosubmit-v2.js will
    // move them back into the form right before AJAX serialization (via
    // Drupal.detachBehaviors('serialize')) — that keeps the Supprimer submit
    // button inside the <form> at the time FormData reads it.
    const movedModals = [];
    rootEl.querySelectorAll('.modal.cat-eamf-modal').forEach((modalEl) => {
      const originalParent = modalEl.parentElement;
      const isConfirmModal = modalEl.classList.contains('cat-eamf-modal--confirm');
      document.body.appendChild(modalEl);
      if (!isConfirmModal) {
        restructureFileModal(modalEl, {
          onView: (url, filename) => setViewer({ url, filename }),
        });
      }
      movedModals.push({ modalEl, originalParent });
    });

    const handleModalHidden = (event) => {
      if (!(event.target instanceof HTMLElement) || !event.target.classList.contains('modal')) return;
      refreshRows();
    };

    // Listen for field changes both on rootEl (native controls) and on each
    // moved modal (now on body) so the React table stays in sync.
    rootEl.addEventListener('input', refreshRows);
    rootEl.addEventListener('change', refreshRows);
    movedModals.forEach(({ modalEl }) => {
      modalEl.addEventListener('input', refreshRows);
      modalEl.addEventListener('change', refreshRows);
    });
    inputEl?.addEventListener('change', handleNativeInputChange);
    document.addEventListener('hidden.bs.modal', handleModalHidden);

    return () => {
      delete rootEl.dataset.cstFileWidgetMounted;
      // Move modals back to their original parents before unmount
      movedModals.forEach(({ modalEl, originalParent }) => {
        modalEl.removeEventListener('input', refreshRows);
        modalEl.removeEventListener('change', refreshRows);
        if (modalEl.isConnected && originalParent?.isConnected) {
          originalParent.appendChild(modalEl);
        } else if (modalEl.isConnected) {
          modalEl.remove();
        }
      });
      rootEl.removeEventListener('input', refreshRows);
      rootEl.removeEventListener('change', refreshRows);
      inputEl?.removeEventListener('change', handleNativeInputChange);
      document.removeEventListener('hidden.bs.modal', handleModalHidden);
    };
  }, [settings]);

  // Auto-upload: when selectedFileName changes and there's a file, submit automatically
  useEffect(() => {
    if (!selectedFileName || isUploading) {
      return;
    }
    submitPendingFile();
  }, [selectedFileName]);

  useEffect(() => {
    if (!rows.length) {
      setIsUploading(false);
    }
  }, [rows]);

  const dynamicFieldLabels = useMemo(() => {
    const labelSet = new Map();
    for (const row of rows) {
      for (const [label, value] of Object.entries(row.fields || {})) {
        if (label.startsWith('__') || !value) continue;
        if (!labelSet.has(label)) {
          labelSet.set(label, label);
        }
      }
    }
    return Array.from(labelSet.keys());
  }, [rows]);

  const columns = useMemo(() => {
    const nextColumns = [{
      id: 'summary',
      header: () => settings?.label || 'Fichier',
      cell: ({ row }) => (
        <div className="cat-file-widget__summary-cell">
          <strong>{row.original.summary}</strong>
        </div>
      ),
    }];

    for (const label of dynamicFieldLabels) {
      nextColumns.push({
        id: `field_${label}`,
        header: () => label,
        cell: ({ row }) => row.original.fields?.[label] || '—',
      });
    }

    nextColumns.push({
      id: '__actions',
      header: () => null,
      cell: ({ row }) => (
        <div className="cat-file-widget__actions">
          <button
            type="button"
            className="cat-file-widget__icon-button"
            title={settings?.editLabel || 'Modifier'}
            data-cst-modal-open={row.original.modalId}
            onClick={(e) => {
              e.stopPropagation();
              openFileModal(row.original.modalId);
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
          </button>
          {row.original.downloadUrl ? (
            <a
              className="cat-file-widget__icon-button"
              title={settings?.downloadLabel || 'Télécharger'}
              href={row.original.downloadUrl}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            </a>
          ) : null}
          {row.original.downloadUrl ? (
            <button
              type="button"
              className="cat-file-widget__icon-button"
              title="Visualiser"
              onClick={(e) => {
                e.stopPropagation();
                setViewer({ url: row.original.downloadUrl, filename: row.original.summary });
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
            </button>
          ) : null}
          <button
            type="button"
            className="cat-file-widget__icon-button cat-file-widget__icon-button--danger"
            title="Supprimer"
            data-cst-modal-open={`${row.original.modalId}-confirm`}
            data-bs-toggle="modal"
            data-bs-target={`#${row.original.modalId}-confirm`}
            onClick={(e) => e.stopPropagation()}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
          </button>
        </div>
      ),
    });

    return nextColumns;
  }, [rows, dynamicFieldLabels, settings]);

  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (!settings) {
    return null;
  }

  return (
    <div className={`cat-file-widget${isUploading ? ' is-uploading' : ''}`}>
      <section className="cat-file-widget__upload">
        <div className="cat-file-widget__upload-copy">
          <p className="cat-file-widget__eyebrow">{settings.label || 'Fichiers'}</p>
          <h3 className="cat-file-widget__title">{settings.uploadLabel}</h3>
          <p className="cat-file-widget__hint">{settings.uploadHint}</p>
          {uploadInfoHtml ? (
            <HtmlContent className="cat-file-widget__upload-info" html={uploadInfoHtml} />
          ) : null}
        </div>

        <div className="cat-file-widget__upload-main">
          <div
            className={`cat-file-widget__dropzone${isDragOver ? ' is-drag-over' : ''}${selectedFileName ? ' has-file' : ''}`}
            role="button"
            tabIndex={isUploading ? -1 : 0}
            onClick={() => {
              if (!isUploading) {
                nativeRefs.current.inputEl?.click();
              }
            }}
            onKeyDown={(event) => {
              if (isUploading) {
                return;
              }

              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                nativeRefs.current.inputEl?.click();
              }
            }}
            onDragEnter={(event) => {
              if (isUploading) {
                return;
              }

              event.preventDefault();
              dragDepthRef.current += 1;
              setIsDragOver(true);
            }}
            onDragOver={(event) => {
              if (isUploading) {
                return;
              }

              event.preventDefault();
              event.dataTransfer.dropEffect = 'copy';
              setIsDragOver(true);
            }}
            onDragLeave={(event) => {
              if (isUploading) {
                return;
              }

              event.preventDefault();
              dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
              if (dragDepthRef.current === 0) {
                setIsDragOver(false);
              }
            }}
            onDrop={handleDrop}
          >
            <span className="cat-file-widget__dropzone-label">
              {isUploading ? (
                <>
                  <span className="cat-file-widget__spinner" />
                  {'Envoi en cours...'}
                </>
              ) : (selectedFileName ? settings.pendingLabel : settings.dropzoneLabel)}
            </span>
            <strong className="cat-file-widget__dropzone-file">
              {selectedFileName || settings.dropzoneHint}
            </strong>
          </div>

          <div className="cat-file-widget__upload-actions">
            {selectedFileName ? (
              <button
                type="button"
                className="cat-react-button cat-react-button--secondary"
                disabled={isUploading}
                onClick={clearPendingFile}
              >
                {settings.resetLabel}
              </button>
            ) : null}
          </div>

          {dropError ? <p className="cat-file-widget__drop-error">{dropError}</p> : null}
        </div>
      </section>

      <section className="cat-file-widget__table-shell">
        <header className="cat-file-widget__table-header">
          <div>
            <p className="cat-file-widget__table-eyebrow">Pieces jointes</p>
            <h4 className="cat-file-widget__table-title">Fichiers ajoutes</h4>
          </div>
          <p className="cat-file-widget__table-count">{rows.length} fichier(s)</p>
        </header>

        {rows.length ? (
          <div className="cat-file-widget__table-frame">
            <table className="cat-file-widget__table cat-eamf__table">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        scope="col"
                        style={header.column.id === '__actions' ? { width: '1%', whiteSpace: 'nowrap' } : undefined}
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => openFileModal(row.original.modalId)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={cell.column.id === '__actions' ? { width: '1%', whiteSpace: 'nowrap' } : undefined}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="cat-file-widget__empty-state">
            <strong>{settings.emptyLabel}</strong>
            <span>{settings.emptyHint}</span>
          </div>
        )}
      </section>

      <FileViewerPortal
        open={Boolean(viewer)}
        url={viewer?.url || ''}
        filename={viewer?.filename || ''}
        onClose={() => setViewer(null)}
      />
    </div>
  );
}
